// A HELI TOUR'S FLIGHT, AS A FUNCTION OF TIME (2026-11-01).
//
// Pure, import-free math, on purpose: the World flies it (components/heli) and
// the API's narration script (scripts/write_heli_tour.js) imports this same file
// to give the pilot a word budget for each place — one timing, two readers.
//
// A flight (data/heli_routes.json) is waypoints; this turns it, for one pad,
// into a TRACK — keys { t, x, y, z } from the pad's ground at t=0, straight up
// to CLIMB_Y, through the waypoints (time shared out by distance: a constant
// cruise), back to hover over the pad, and down onto it at `seconds`. The
// helicopter's pose at any moment is a time-keyed Catmull-Rom through those keys
// (sampleTrack), turned by where it is going, nose down when it hurries, banked
// into its turns. Everyone aboard (and everyone watching from the street) asks
// the same function with the same server time, so they all see the same flight.

export const TAKEOFF_S = 7;     // pad → hover over it
export const LAND_S = 9;        // hover over the pad → down on it
export const CLIMB_Y = 9;       // the hover height (world units: ≈20m)
export const CUE_LEAD_S = 3;    // a place is spoken about this long before it is reached
export const WORDS_PER_S = 2.3; // a pilot's unhurried speaking rate, for budgets

/**
 * The flight's keys for `pad` ({ x, z }): [{ t, x, y, z }], first and last on the
 * pad's ground. Each key also carries the waypoint it came from (`wp`, an index
 * into flight.route) where there is one.
 */
export function buildTrack(flight, pad) {
  const seconds = Number(flight.seconds) || 90;
  const cruise = Number(flight.cruise) || 22;
  // `weight` (default 1) stretches the leg INTO a waypoint: a slow pass over a
  // close cluster of places, so each still gets time to be talked about.
  const route = (flight.route || []).map((w, i) => ({ x: w.x, y: Number.isFinite(w.y) ? w.y : cruise, z: w.z, wp: i, weight: Number(w.weight) > 0 ? Number(w.weight) : 1 }));
  const climb = { x: pad.x, y: CLIMB_Y, z: pad.z };
  const over = { x: pad.x, y: CLIMB_Y, z: pad.z };
  const legs = [climb, ...route, over];
  // Time between the climb and the hover, shared out by horizontal distance.
  const dist = [0];
  for (let i = 1; i < legs.length; i += 1) {
    const d = Math.hypot(legs[i].x - legs[i - 1].x, legs[i].z - legs[i - 1].z) + Math.abs(legs[i].y - legs[i - 1].y) * 0.5;
    dist.push(dist[i - 1] + d * (legs[i].weight || 1));
  }
  const total = dist[dist.length - 1] || 1;
  const t0 = TAKEOFF_S; const t1 = seconds - LAND_S;
  const keys = [{ t: 0, x: pad.x, y: 0, z: pad.z }];
  legs.forEach((p, i) => keys.push({ t: t0 + (t1 - t0) * (dist[i] / total), x: p.x, y: p.y, z: p.z, ...(p.wp != null ? { wp: p.wp } : {}) }));
  keys.push({ t: seconds, x: pad.x, y: 0, z: pad.z });
  return { keys, seconds, padYaw: Number(pad.rotY) || 0 };
}

// Hermite on time-keyed points: tangents from the neighbours (zero at the ends —
// it lifts off from rest and settles onto the pad).
const plumb = (a, b) => Math.hypot(b.x - a.x, b.z - a.z) < 1e-3;
function tangent(keys, i, axis) {
  if (i <= 0 || i >= keys.length - 1) return 0;
  const a = keys[i - 1]; const b = keys[i + 1];
  // ⚠ THE HOVER IS A STOP. Either side of the climb (and of the hover before the
  // let-down) is a plumb leg with no sideways speed; a sideways tangent here would
  // have it arrive at the hover still moving and stop dead (2026-11-02, the jolt
  // at the top of the climb). Sideways it starts from rest and comes to rest.
  if (axis !== 'y' && (plumb(a, keys[i]) || plumb(keys[i], b))) return 0;
  return (b[axis] - a[axis]) / Math.max(1e-6, b.t - a.t);
}
function hermite(keys, t, axis) {
  if (t <= keys[0].t) return keys[0][axis];
  const n = keys.length;
  if (t >= keys[n - 1].t) return keys[n - 1][axis];
  let i = 0;
  while (i < n - 2 && keys[i + 1].t < t) i += 1;
  const a = keys[i]; const b = keys[i + 1];
  const h = Math.max(1e-6, b.t - a.t);
  const s = (t - a.t) / h;
  const s2 = s * s; const s3 = s2 * s;
  // A takeoff or a landing leg is vertical: no tangent sideways, so it rises
  // and sets down plumb instead of swinging off the pad.
  const vertical = Math.hypot(b.x - a.x, b.z - a.z) < 1e-3;
  const ma = vertical && axis !== 'y' ? 0 : tangent(keys, i, axis) * h;
  const mb = vertical && axis !== 'y' ? 0 : tangent(keys, i + 1, axis) * h;
  return (2 * s3 - 3 * s2 + 1) * a[axis] + (s3 - 2 * s2 + s) * ma + (-2 * s3 + 3 * s2) * b[axis] + (s3 - s2) * mb;
}
const posAt = (keys, t) => ({ x: hermite(keys, t, 'x'), y: hermite(keys, t, 'y'), z: hermite(keys, t, 'z') });
const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));

/**
 * The helicopter at `t` seconds into the flight: { x, y, z, yaw, pitch, roll,
 * speed, phase } — yaw 0 = facing +z (the scene's convention), phase 'ground' |
 * 'takeoff' | 'cruise' | 'landing' | 'done'. Before 0 it sits on the pad.
 */
const smooth01 = (x) => { const k = Math.max(0, Math.min(1, x)); return k * k * (3 - 2 * k); };
/** Where it is heading at `t`: the way it moves across the ground (a central difference), or null if it is not. */
function headingAt(keys, seconds, t, h = 0.5) {
  const a = posAt(keys, Math.max(0, t - h)); const b = posAt(keys, Math.min(seconds, t + h));
  const dx = b.x - a.x; const dz = b.z - a.z;
  return Math.hypot(dx, dz) > 1e-3 ? Math.atan2(dx, dz) : null;
}
function speedAt(keys, seconds, t, h = 0.25) {
  const a = posAt(keys, Math.max(0, t - h)); const b = posAt(keys, Math.min(seconds, t + h));
  return Math.hypot(b.x - a.x, b.z - a.z) / (2 * h);
}
/** The cruise heading: the way it moves — over a longer stretch when slow, so a slow
 *  turn is spread over seconds. (A route should never double straight back on itself:
 *  that is a dead stop and a spin, which no heading can make smooth — data/heli_routes.)*/
function cruiseYaw(keys, seconds, t, fallback) {
  const near = headingAt(keys, seconds, t, 0.5); const wide = headingAt(keys, seconds, t, 2.5);
  if (near == null) return wide ?? fallback;
  if (wide == null) return near;
  return near + wrap(wide - near) * smooth01(1 - speedAt(keys, seconds, t) / 4);
}

export function sampleTrack(track, t) {
  const { keys, seconds, padYaw } = track;
  const tt = Math.max(0, Math.min(seconds, t));
  const p = posAt(keys, tt);
  /* ⚠ HEADING, CONTINUOUS IN TIME (2026-11-02). Khari: "there is still a slower
     stutter that happens during liftoff and landing, when the helicopter is close
     to the landing pad". The heading used to be found by searching AHEAD (and on
     the way down, BEHIND) in 0.6 s steps for the first point 0.6 u off — over a
     plumb climb that point creeps along the departure arc and jumps back each
     0.6 s, a sawtooth: measured, up to 72° from one frame to the next on lift-off
     and 28° on the let-down, and the camera turned with it. Now:
       climbing   the pad's heading turned, smoothly over the climb, onto the
                  first leg's (a pedal turn on the way up);
       cruising   the way it moves (a central difference — continuous; over a
                  longer stretch when slow, so a turn-back is a pedal turn);
       letting down  the approach's heading turned smoothly back onto the pad's.
     Each hand-over meets the next: the climb ends on the departure heading,
     which is what the central difference reads at that instant, and the same at
     the hover before the let-down. */
  const t0 = TAKEOFF_S; const t1 = seconds - LAND_S;
  const depart = cruiseYaw(keys, seconds, t0, padYaw);
  const approach = cruiseYaw(keys, seconds, t1, padYaw);
  let yaw;
  if (tt <= t0) yaw = padYaw + wrap(depart - padYaw) * smooth01(tt / t0);
  else if (tt >= t1) yaw = approach + wrap(padYaw - approach) * smooth01((tt - t1) / LAND_S);
  else yaw = cruiseYaw(keys, seconds, tt, tt - t0 < t1 - tt ? depart : approach);
  // Speed and turn, by finite difference.
  const dt = 0.25;
  const p1 = posAt(keys, Math.min(seconds, tt + dt));
  const p0 = posAt(keys, Math.max(0, tt - dt));
  const speed = Math.hypot(p1.x - p0.x, p1.z - p0.z) / (2 * dt);
  const h0 = Math.atan2(p.x - p0.x, p.z - p0.z); const h1 = Math.atan2(p1.x - p.x, p1.z - p.z);
  // The bank fades in with speed instead of switching on at a threshold (a hover's
  // heading swings fast at walking pace — that, times a gate, was a 34° snap).
  const turn = speed > 0.05 ? wrap(h1 - h0) / dt : 0;
  const pitch = Math.max(-0.22, Math.min(0.05, -speed * 0.03));     // nose down with speed
  const roll = Math.max(-0.3, Math.min(0.3, -turn * speed * 0.12)) * smooth01((speed - 0.3) / 2.5);  // into the turn (≤17°)
  const phase = t < 0 ? 'ground' : t >= seconds ? 'done' : tt < TAKEOFF_S ? 'takeoff' : tt > seconds - LAND_S ? 'landing' : 'cruise';
  return { ...p, yaw, pitch, roll, speed, phase };
}

/**
 * When the pilot speaks, and for how long they have: [{ t, until, kind, cue,
 * wp, words }] — kind 'intro' (lifting off), 'place' (a waypoint's `cue`),
 * 'event' (the what's-on slot), 'outro' (coming in to land). `words` is the
 * budget: the time to the next one at WORDS_PER_S, less a breath.
 */
export function speakingPlan(flight, track) {
  const out = [{ t: 1, kind: 'intro' }];
  for (const k of track.keys) {
    if (k.wp == null) continue;
    const w = flight.route[k.wp];
    if (w?.cue) out.push({ t: Math.max(2, k.t - CUE_LEAD_S), kind: 'place', cue: w.cue, wp: k.wp });
    else if (w?.event) out.push({ t: Math.max(2, k.t - CUE_LEAD_S), kind: 'event', wp: k.wp });
  }
  out.push({ t: track.seconds - LAND_S - 1.5, kind: 'outro' });
  out.sort((a, b) => a.t - b.t);
  out.forEach((s, i) => {
    s.until = i + 1 < out.length ? out[i + 1].t : track.seconds;
    s.words = Math.max(6, Math.floor((s.until - s.t - 0.8) * WORDS_PER_S));
  });
  return out;
}
